import React from 'react';
import useStyles from './styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import * as IconMDI from 'mdi-material-ui';
import { TitleSearchbar, UserCard } from 'molecules';
import { Element, Link } from 'react-scroll';
import {
  AlphabetizedUser,
  CreativeDirectory,
  CreativeDirectoryUserResult,
} from 'types/creativeDirectory';
import { QueryResult, ScrollToTop } from '@elevator/ui';

/**
 * Query variables type for USER_DIRECTORY query
 */
type UserDirectoryVariables = {
  creditTypes?: Array<string>;
  searchInput?: string;
};

const scrollKey = (char: string) => char.toLowerCase();

/** When `userDirectory` is missing or failed, still show A–Z (all inactive until data loads). */
const FALLBACK_ALPHABET: AlphabetizedUser[] = 'abcdefghijklmnopqrstuvwxyz'
  .split('')
  .map((char) => ({ char, results: [] as CreativeDirectoryUserResult[] }));

/**
 * Directory Component
 *
 * Searchable directory with credit-type chips, A–Z jump links, and letter sections with user cards.
 * The chrome (search, chips, alphabet) stays mounted; loading and errors appear in the list region.
 */
const Directory = (props: DirectoryPropTypes) => {
  const { classes } = useStyles();

  const {
    data,
    refetch,
    searchInput,
    setSearchInput,
    activeLabels,
    setActiveLabels,
    variables,
    error,
    queryLoading,
    filterCreditTypes,
  } = props;

  /** Only `userDirectory` is required to render the list — `creditTypes` can be missing in partial/error responses. */
  const hasDirectoryPayload = data != null && Array.isArray(data.userDirectory);

  const userDirectory = data?.userDirectory ?? [];
  const roles =
    filterCreditTypes != null
      ? filterCreditTypes
      : Array.isArray(data?.creditTypes)
        ? data.creditTypes
        : [];

  /** Show loading even when Apollo still holds a stale `error` from a prior attempt (e.g. refetch). */
  const showListSkeleton = Boolean(queryLoading && !hasDirectoryPayload);
  /** Only treat as failure once the query is settled; avoids flashing the error string during loading. */
  const showListError = Boolean(!hasDirectoryPayload && error && !queryLoading);

  /**
   * Handles chip click to toggle credit type filter
   */
  const handleClickChip = (typeId: string) => {
    const typeIdIndex = activeLabels.findIndex(
      (currentTypeId) => currentTypeId === typeId,
    );
    const newActiveLabels =
      typeIdIndex === -1
        ? [...activeLabels, typeId]
        : activeLabels.filter((_, index) => index !== typeIdIndex);
    setActiveLabels(newActiveLabels);
  };

  const hasNoResults = () =>
    userDirectory.length > 0 &&
    userDirectory.every((directoryItem) => directoryItem.results.length === 0);

  const handleSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const newVariables: UserDirectoryVariables = {
      ...variables,
      searchInput: searchInput || undefined,
    };
    void refetch?.(newVariables);
  };

  const handleClear = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    setSearchInput('');
    const newVariables: UserDirectoryVariables = {
      ...variables,
      searchInput: undefined,
    };
    void refetch?.(newVariables);
  };

  const renderNoSearchResults = () => (
    <Stack
      className={classes.noResultsContainer}
      alignItems="center"
      justifyContent="center"
    >
      <Stack className={classes.noResultsContent} alignItems="center" spacing={1}>
        <IconMDI.Alien fontSize="large" />
        <Typography variant="h6" className={classes.noResultsIcon}>
          No results found.
        </Typography>
      </Stack>
    </Stack>
  );

  const renderAlphabetTabs = () => {
    const alphabetRows =
      hasDirectoryPayload && userDirectory.length > 0 ? userDirectory : FALLBACK_ALPHABET;

    return (
      <Box className={classes.cardContainer}>
        <Box className={classes.alphabetContainer}>
          {alphabetRows.map(({ char, results }, index) => {
            const key = scrollKey(char);
            return (
              <Box className={classes.letterBox} key={`${key}-${index}`}>
                {results.length > 0 ? (
                  <Link to={key} smooth={true} offset={-100} className={classes.letter}>
                    {char.toUpperCase()}
                  </Link>
                ) : (
                  <Typography className={classes.disabledLetter}>
                    {char.toUpperCase()}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  const renderListSkeleton = () => (
    <Box
      className={classes.listSkeletonRoot}
      aria-busy="true"
      aria-label="Loading directory"
    >
      <Grid container spacing={2} className={classes.gridContainer}>
        {Array.from({ length: 9 }).map((_, i) => (
          <Grid item xs={12} sm={6} md={4} key={i}>
            <Skeleton
              variant="rectangular"
              height={140}
              className={classes.cardSkeleton}
              animation="wave"
              sx={(theme) => ({ bgcolor: theme.palette.action.hover })}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderListError = () => (
    <Stack
      className={classes.noResultsContainer}
      alignItems="center"
      justifyContent="center"
    >
      <Typography variant="body1" align="center" color="text.secondary">
        Unable to load directory results. Please try again later.
      </Typography>
    </Stack>
  );

  /**
   * Renders user results organized by alphabet sections
   */
  const renderUserResults = () => {
    return (
      <List className={classes.resultsList}>
        {userDirectory.map(({ char, results }, sectionIndex) => {
          if (results.length === 0) {
            return null;
          }

          const key = scrollKey(char);

          return (
            <React.Fragment key={key}>
              {sectionIndex > 0 ? <Divider className={classes.sectionDivider} /> : null}
              <Element name={key}>
                <ListItem className={classes.alphabetSection} disablePadding>
                  <Box className={classes.sectionBlock}>
                    <Typography
                      variant="h4"
                      className={classes.letterHeader}
                      component="h2"
                    >
                      {char.toUpperCase()}
                    </Typography>
                    <Grid container spacing={2} className={classes.gridContainer}>
                      {results.map((user: CreativeDirectoryUserResult) => (
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={4}
                          className={classes.userCardContainer}
                          key={user.preferredUsername}
                        >
                          <UserCard
                            variant="fullWidth"
                            user={user}
                            name={user.displayName || user.preferredUsername}
                            socialMedia={user.socialMedia}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </ListItem>
              </Element>
            </React.Fragment>
          );
        })}
      </List>
    );
  };

  const renderListBody = () => {
    if (showListSkeleton) {
      return renderListSkeleton();
    }
    if (showListError) {
      return renderListError();
    }
    if (hasDirectoryPayload && hasNoResults()) {
      return renderNoSearchResults();
    }
    if (hasDirectoryPayload) {
      return renderUserResults();
    }
    return null;
  };

  return (
    <Box className={classes.root} data-layout="directory">
      <Box className={classes.outerCard}>
        <Stack
          className={classes.searchSection}
          direction="column"
          alignItems="center"
          spacing={2}
        >
          <Box className={classes.searchBar}>
            <TitleSearchbar
              onChange={(event) => setSearchInput(event.currentTarget.value)}
              onClear={handleClear}
              handleSubmit={handleSearch}
              id={'search'}
              value={searchInput}
              placeholder={'Search Creator Directory'}
            />
          </Box>

          {roles.length > 0 ? (
            <Box className={classes.chipList}>
              {roles.map(({ typeName, typeId }) => (
                <Chip
                  key={typeId}
                  label={typeName}
                  onClick={() => handleClickChip(typeId)}
                  color={activeLabels.includes(typeId) ? 'primary' : 'default'}
                />
              ))}
            </Box>
          ) : null}
        </Stack>
        {renderAlphabetTabs()}

        <Box className={classes.listRegion}>{renderListBody()}</Box>
      </Box>
      <ScrollToTop visibleYOffset={70} />
    </Box>
  );
};

export interface DirectoryPropTypes
  extends QueryResult<'data', CreativeDirectory, UserDirectoryVariables> {
  searchInput: string;
  setSearchInput: (value: string) => void;
  activeLabels: Array<string>;
  setActiveLabels: (labels: Array<string>) => void;
  variables: UserDirectoryVariables;
  /** From the `userDirectory` query’s `creditTypes` field so chips show when the list errors. */
  filterCreditTypes?: Array<{ typeId: string; typeName: string }>;
  /** Apollo query `loading` — refetches keep UI via `previousData` in index. */
  queryLoading?: boolean;
}

export default Directory;
